import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemButton,
  LinearProgress,
  Chip
} from '@mui/material';
import axios from 'axios';
import MultiplayerApp from './MultiplayerApp';
import { API_BASE_URL } from './config';

// Configure axios to send credentials
axios.defaults.withCredentials = true;

interface AnimeOpening {
  id: number;
  title: string;
  videoId: string;
  startTime: number;
  endTime: number;
  difficulty?: string;
  sessionId?: string;
}

interface SearchResult {
  id: number;
  title: string;
}

interface GameStats {
  totalOpenings: number;
  currentOpening: number;
  score: number;
  correctGuesses: number;
}

interface AppProps {
  defaultMode?: 'menu' | 'singleplayer' | 'multiplayer';
  onNavigateHome?: () => void;
}

function App({ defaultMode = 'menu', onNavigateHome }: AppProps) {
  const [gameMode, setGameMode] = useState<'menu' | 'singleplayer' | 'multiplayer'>(defaultMode);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [numberOfOpenings, setNumberOfOpenings] = useState(10);
  const [gameStats, setGameStats] = useState<GameStats>({
    totalOpenings: 0,
    currentOpening: 0,
    score: 0,
    correctGuesses: 0
  });
  const [currentOpening, setCurrentOpening] = useState<AnimeOpening | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<any>(null);
  const isApiReady = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [usedVideoIds, setUsedVideoIds] = useState<Set<string>>(new Set());
  const loadingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [guessTime, setGuessTime] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(['easy', 'medium']);
  const [pointBreakdown, setPointBreakdown] = useState<{base: number, time: number, difficulty: string} | null>(null);
  
  // Keyboard navigation state
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset state when navigating via browser back button
  useEffect(() => {
    if (defaultMode === 'singleplayer' && !gameStarted && !gameFinished) {
      // Clean up any existing player
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.log('Player cleanup error:', e);
        }
        playerRef.current = null;
      }
      
      // Reset all game state
      setCurrentOpening(null);
      setUsedVideoIds(new Set());
      setRetryCount(0);
      setLoading(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedIndex(0);
      setShowAnswer(false);
      setIsCorrect(false);
      setHasGuessed(false);
      setIsPlaying(false);
      setTimeLeft(15);
      setSessionId(null);
      
      // Clear any timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }
    
    return () => {
      // Cleanup on unmount
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.log('Player cleanup error on unmount:', e);
        }
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [defaultMode]);

  // Initialize YouTube API
  useEffect(() => {
    // Check if YouTube API is already loaded
    if (window.YT && window.YT.Player) {
      isApiReady.current = true;
    } else if (!isApiReady.current) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        isApiReady.current = true;
        // Don't automatically fetch opening - wait for user to start game
      };
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.log('Player cleanup error:', e);
        }
        playerRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = undefined;
      }
    };
  }, []);

  // Handle game start
  const handleStartGame = (openings: number = numberOfOpenings) => {
    // Clean up any existing state first
    if (playerRef.current) {
      try {
        playerRef.current.pauseVideo();
        playerRef.current.destroy();
      } catch (e) {
        console.log('Player cleanup on game start:', e);
      }
      playerRef.current = null;
    }
    
    // Ensure the player div exists
    const playerContainer = document.querySelector('.video-container');
    if (playerContainer && !document.getElementById('youtube-player')) {
      const playerDiv = document.createElement('div');
      playerDiv.id = 'youtube-player';
      playerContainer.appendChild(playerDiv);
    }
    
    // Clear any existing timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = undefined;
    }
    
    // Reset ALL game state
    setCurrentOpening(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowAnswer(false);
    setLoading(false);
    setTimeLeft(15);
    setIsPlaying(false);
    setIsTimeUp(false);
    setIsCorrect(false);
    setHasGuessed(false);
    setRetryCount(0);
    setUsedVideoIds(new Set());
    setGuessTime(null);
    setSessionId(null);
    setPointBreakdown(null);
    setSelectedIndex(0);
    
    // Now set the new game state
    setGameStarted(true);
    setGameFinished(false);
    setLoading(true); // Set loading true immediately
    setGameStats({
      totalOpenings: openings,
      currentOpening: 1,
      score: 0,
      correctGuesses: 0
    });
    
    // Small delay to ensure state is settled before fetching
    setTimeout(() => {
      if (isApiReady.current) {
        fetchRandomOpening();
      } else {
        // If API isn't ready yet, wait for it
        const checkApiReady = setInterval(() => {
          if (isApiReady.current) {
            clearInterval(checkApiReady);
            fetchRandomOpening();
          }
        }, 100);
      }
    }, 100);
  };

  // Handle quickstart
  const handleQuickStart = () => {
    handleStartGame(10);
  };

  // Calculate score based on correctness and speed
  const calculateScore = (correct: boolean, timeRemaining: number, difficulty: string = 'hard'): number => {
    if (!correct) return 0;
    
    const basePoints = difficulty === 'easy' ? 10 : difficulty === 'medium' ? 15 : 20;
    const timeBonus = timeRemaining >= 12 ? 5 : // 3 seconds or less (15-12=3)
                     timeRemaining >= 10 ? 4 : // 5 seconds or less
                     timeRemaining >= 8 ? 3 :  // 7 seconds or less
                     timeRemaining >= 6 ? 2 :  // 9 seconds or less
                     timeRemaining >= 4 ? 1 : 0; // 11 seconds or less
    
    return basePoints + timeBonus;
  };

  // Handle next opening or end game
  const handleNextOpening = () => {
    if (gameStats.currentOpening >= gameStats.totalOpenings) {
      // Game finished
      setGameFinished(true);
      setGameStarted(false);
    } else {
      // Next opening
      setGameStats(prev => ({
        ...prev,
        currentOpening: prev.currentOpening + 1
      }));
      setSelectedIndex(0); // Reset keyboard navigation for new opening
      fetchRandomOpening();
    }
  };

  // Reset game
  const handleResetGame = () => {
    // Clean up player
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.log('Player cleanup on reset:', e);
      }
      playerRef.current = null;
    }
    
    // Clear timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = undefined;
    }
    
    // Reset all state
    setGameStarted(false);
    setGameFinished(false);
    setCurrentOpening(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowAnswer(false);
    setLoading(false);
    setTimeLeft(15);
    setIsPlaying(false);
    setIsTimeUp(false);
    setIsCorrect(false);
    setHasGuessed(false);
    setRetryCount(0);
    setUsedVideoIds(new Set());
    setGuessTime(null);
    setSessionId(null);
    setPointBreakdown(null);
    setSelectedIndex(0);
  };

  // Back to main menu
  const handleBackToMenu = () => {
    // Clean up player first
    if (playerRef.current) {
      try {
        playerRef.current.pauseVideo();
        playerRef.current.destroy();
      } catch (e) {
        console.log('Player cleanup on menu return:', e);
      }
      playerRef.current = null;
    }
    
    // Clear all timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = undefined;
    }
    
    // Reset game state
    handleResetGame();
    
    // Navigate back
    if (onNavigateHome) {
      onNavigateHome();
    } else {
      setGameMode('menu');
    }
  };

  // Handle player creation and updates
  useEffect(() => {
    if (!isApiReady.current || !currentOpening || !gameStarted) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('Creating YouTube player for:', currentOpening.videoId);
    }

    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.log('Player destroy error:', e);
      }
      playerRef.current = null;
    }

    setTimeLeft(15);
    setIsPlaying(true);
    setIsTimeUp(false);
    setGuessTime(null);
    // Keep loading state true until player is ready

    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    // Auto-focus search input on desktop (not mobile)
    const isMobile = window.innerWidth <= 768;
    if (!isMobile && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    // Set a 5-second timeout for loading (increased to give more time)
    loadingTimeoutRef.current = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Video loading timeout for:', currentOpening?.videoId, 'Retry count:', retryCount);
      }
      if (retryCount < 5) { // Reduced retry limit to avoid infinite loops
        setRetryCount(prev => prev + 1);
        // Add the problematic video to used list to avoid it in future
        if (currentOpening) {
          setUsedVideoIds(prev => new Set(Array.from(prev).concat(currentOpening.videoId)));
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('Retrying with different video...');
        }
        fetchRandomOpening();
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Too many loading timeouts, skipping to next opening');
        }
        // Set loading to false and show error state
        setLoading(false);
        setRetryCount(0);
        setCurrentOpening(null);
      }
    }, 5000);

    // Create a new div for the player if it doesn't exist
    const playerDiv = document.getElementById('youtube-player');
    if (!playerDiv) {
      console.error('YouTube player div not found');
      setLoading(false);
      return;
    }
    
    // Clear the div content to ensure clean state
    playerDiv.innerHTML = '';
    
    try {
      playerRef.current = new window.YT.Player('youtube-player', {
      height: '315',
      width: '560',
      videoId: currentOpening.videoId,
      playerVars: {
        autoplay: 1,
        controls: 1,
        disablekb: 0,
        fs: 1,
        modestbranding: 1,
        rel: 0,
        start: currentOpening.startTime,
        // Remove end parameter to let video continue playing
        playsinline: 1,
        showinfo: 0,
        iv_load_policy: 3,
        cc_load_policy: 0,
        origin: window.location.origin,
        mute: 0,
        enablejsapi: 1
      },
      events: {
        onStateChange: (event: any) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Player state changed:', event.data, 'for video:', currentOpening?.videoId);
          }
          if (event.data === window.YT.PlayerState.PLAYING) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video started playing successfully');
            }
            // Clear timeout since video is playing
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }
            setRetryCount(0);
          } else if (event.data === window.YT.PlayerState.ENDED) {
            // Video ended naturally - don't stop it since we want it to continue playing
            setIsPlaying(false);
          } else if (event.data === window.YT.PlayerState.UNSTARTED) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video unstarted - may indicate loading issues');
            }
          } else if (event.data === window.YT.PlayerState.BUFFERING) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video buffering...');
            }
          }
        },
        onReady: (event: any) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('YouTube player ready for video:', currentOpening?.videoId);
          }
          // Clear loading timeout since video loaded successfully
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }
          // Reset retry count on successful load
          setRetryCount(0);
          // Track this video as used
          if (currentOpening) {
            setUsedVideoIds(prev => new Set(Array.from(prev).concat(currentOpening.videoId)));
          }
          // Set volume to 100%
          try {
            event.target.setVolume(100);
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Could not set volume:', e);
            }
          }
          
          // Check if video is actually playable
          const playerState = event.target.getPlayerState();
          if (process.env.NODE_ENV === 'development') {
            console.log('Player state on ready:', playerState);
          }
          
          // Start countdown timer
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
              if (prev <= 0) {
                clearInterval(timerRef.current);
                setIsTimeUp(true);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        },
        onError: (event: any) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('YouTube Player Error:', event.data, 'for video:', currentOpening?.videoId);
          }
          // Clear loading timeout since we got an error
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }
          
          // Add the problematic video to used list to avoid it in future
          if (currentOpening) {
            setUsedVideoIds(prev => new Set(Array.from(prev).concat(currentOpening.videoId)));
          }
          
          // Auto-skip unavailable videos, but prevent infinite loops
          if (retryCount < 10) { // Increased retry limit
            setRetryCount(prev => prev + 1);
            if (process.env.NODE_ENV === 'development') {
              console.log(`Video unavailable (error ${event.data}), trying next one... (${retryCount + 1}/10)`);
            }
            fetchRandomOpening();
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.error('Too many video errors, skipping to next opening');
            }
            // Set loading to false and show error state
            setLoading(false);
            setRetryCount(0);
            setCurrentOpening(null);
          }
        }
      }
    });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to create YouTube player:', error);
      }
      setLoading(false);
      if (retryCount < 5) {
        setRetryCount(prev => prev + 1);
        fetchRandomOpening();
      } else {
        setRetryCount(0);
        setCurrentOpening(null);
      }
    }
  }, [currentOpening?.videoId, gameStarted]);

  const fetchRandomOpening = async () => {
    try {
      // Only set loading true initially, not on retries
      if (retryCount === 0) {
        setLoading(true);
      }
      
      // Clear timer first to prevent conflicts
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
      
      // Reset all states before fetching
      setShowAnswer(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedIndex(0); // Reset keyboard navigation
      setIsTimeUp(false);
      setIsCorrect(false);
      setHasGuessed(false);
      setIsPlaying(false);
      setTimeLeft(15);
      setPointBreakdown(null);
      // Don't reset retryCount here - let it persist for the current opening attempt
      
      let attempts = 0;
      let newOpening = null;
      
      // Use sessionId to get unique anime titles and include difficulty filter
      // Special handling for hard difficulty: if hard is selected with other difficulties,
      // only include hard 1/3 of the time
      let difficultiesForRequest = [...selectedDifficulties];
      
      if (selectedDifficulties.includes('hard') && selectedDifficulties.length > 1) {
        // Hard is selected along with other difficulties
        // 1/3 chance to actually include hard
        if (Math.random() > 0.33) {
          // 2/3 of the time, remove hard from the request
          difficultiesForRequest = difficultiesForRequest.filter(d => d !== 'hard');
        }
      }
      
      const difficultyParam = difficultiesForRequest.join(',');
      // On first opening of a new game, don't use old sessionId
      const isFirstOpening = gameStats.currentOpening === 1 && !sessionId;
      const url = sessionId && !isFirstOpening
        ? `${API_BASE_URL}/random-opening?sessionId=${sessionId}&difficulties=${difficultyParam}`
        : `${API_BASE_URL}/random-opening?difficulties=${difficultyParam}`;
      
      const response = await axios.get(url);
      
      if (response.data && response.data.videoId) {
        newOpening = response.data;
        // Update sessionId if we got a new one
        if (response.data.sessionId) {
          setSessionId(response.data.sessionId);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Invalid opening data received:', response.data);
        }
      }
      
      if (newOpening) {
        setCurrentOpening(newOpening);
        // Set loading to false immediately after setting the opening
        // The YouTube player will handle its own loading state
        setLoading(false);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Could not get valid opening data');
        }
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error fetching opening:', error);
      console.error('Error details:', {
        apiUrl: `${API_BASE_URL}/random-opening`,
        sessionId: sessionId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        retryCount: retryCount,
        selectedDifficulties: selectedDifficulties
      });
      
      // Instead of showing an alert, try to handle the error gracefully
      if (retryCount < 3) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Network error, retrying...');
        }
        setRetryCount(prev => prev + 1);
        // Retry after a short delay
        setTimeout(() => {
          fetchRandomOpening();
        }, 1000);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Multiple network errors, unable to load opening');
        }
        setLoading(false); // Only set loading false when we give up
        setRetryCount(0);
        // Don't automatically skip to next - let user retry manually
        setCurrentOpening(null);
      }
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setSelectedIndex(0); // Reset selection when search changes
    if (query.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/search?q=${query}`);
        setSearchResults(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error searching:', error);
        }
      }
    } else {
      setSearchResults([]);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isTimeUp || hasGuessed || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          handleGuess(searchResults[selectedIndex].title);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSearchResults([]);
        setSearchQuery('');
        break;
    }
  };

  const handleGuess = async (title: string) => {
    setHasGuessed(true);
    setGuessTime(15 - timeLeft); // Record how long it took to guess
    
    const correct = currentOpening && title.toLowerCase() === currentOpening.title.toLowerCase();
    setIsCorrect(!!correct);
    setIsTimeUp(true);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Calculate score using the API endpoint
    try {
      const response = await axios.post(`${API_BASE_URL}/calculate-score`, {
        correct: !!correct,
        timeRemaining: timeLeft,
        difficulty: currentOpening?.difficulty || 'hard'
      });
      
      const { points, breakdown } = response.data;
      
      setGameStats(prev => ({
        ...prev,
        score: prev.score + points,
        correctGuesses: correct ? prev.correctGuesses + 1 : prev.correctGuesses
      }));
      
      setPointBreakdown(breakdown);
      if (process.env.NODE_ENV === 'development') {
        console.log('API response breakdown:', breakdown);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error calculating score:', error);
      }
      // Fallback to local calculation with proper breakdown
      const difficulty = currentOpening?.difficulty || 'hard';
      const points = calculateScore(!!correct, timeLeft, difficulty);
      
      setGameStats(prev => ({
        ...prev,
        score: prev.score + points,
        correctGuesses: correct ? prev.correctGuesses + 1 : prev.correctGuesses
      }));
      
      // Create breakdown manually for fallback
      if (correct) {
        const basePoints = difficulty === 'easy' ? 10 : difficulty === 'medium' ? 15 : 20;
        const timeBonus = points - basePoints;
        setPointBreakdown({
          base: basePoints,
          time: timeBonus,
          difficulty: difficulty
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('Fallback breakdown created:', { base: basePoints, time: timeBonus, difficulty });
        }
      } else {
        setPointBreakdown(null);
      }
    }
    
    setSearchQuery('');
    setSearchResults([]);
    setSelectedIndex(0); // Reset keyboard navigation
  };

  // Handle multiplayer mode
  if (gameMode === 'multiplayer') {
    return <MultiplayerApp onBackToMenu={() => setGameMode('menu')} />;
  }

  return (
    <>
      <Container maxWidth="md">
        {/* Title header */}
        <div className="title-header">
          <Typography variant="h3" component="h1">
            Anime Opening Quiz
          </Typography>
        </div>

        <Box className="quiz-container">
        {gameMode === 'menu' ? (
          // Main menu
          <div className="quiz-paper">
            <Box className="landing-page">
              <Typography variant="h4" gutterBottom color="primary" className="main-title">
                Test Your Anime Knowledge!
              </Typography>
              <Typography variant="body1" className="landing-description" color="text.secondary">
                Listen to anime opening clips and guess the correct anime title. 
                Choose between single player or multiplayer mode!
              </Typography>
              
              <Box className="game-mode-section">
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={() => setGameMode('singleplayer')}
                  className="start-game-button"
                >
                  Single Player
                </Button>
                
                <Button 
                  variant="outlined" 
                  size="large" 
                  onClick={() => setGameMode('multiplayer')}
                  className="start-game-button"
                >
                  Multiplayer
                </Button>
              </Box>
            </Box>
          </div>
        ) : !gameStarted && !gameFinished ? (
          // Landing page with start button
          <div className="quiz-paper">
            <Box className="landing-page">
              <Typography variant="h4" gutterBottom color="primary" className="main-title">
                Test Your Anime Knowledge!
              </Typography>
              <Typography variant="body1" className="landing-description" color="text.secondary">
                Listen to anime opening clips and guess the correct anime title. 
                You have 15 seconds per opening to make your guess. Can you recognize 
                your favorite anime just from the opening music?
              </Typography>
              
              {/* Game Mode Selection */}
              <Box className="game-mode-section">
                {/* Quick Start Button */}
                <Box className="quick-start-container">
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={() => handleStartGame(10)}
                    className="start-game-button quick-start-button"
                  >
                    Quick Start (10 Openings)
                  </Button>
                </Box>

                {/* Difficulty Selection */}
                <Typography variant="h6" gutterBottom className="custom-section-title">
                  Difficulty Settings
                </Typography>
                <Box className="difficulty-selection">
                  <Box className="difficulty-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
                    <Button
                      variant={selectedDifficulties.includes('easy') ? 'contained' : 'outlined'}
                      color="success"
                      onClick={() => {
                        if (selectedDifficulties.includes('easy')) {
                          setSelectedDifficulties(prev => prev.filter(d => d !== 'easy'));
                        } else {
                          setSelectedDifficulties(prev => [...prev, 'easy']);
                        }
                      }}
                      className="difficulty-button"
                    >
                      Easy
                    </Button>
                    <Button
                      variant={selectedDifficulties.includes('medium') ? 'contained' : 'outlined'}
                      color="warning"
                      onClick={() => {
                        if (selectedDifficulties.includes('medium')) {
                          setSelectedDifficulties(prev => prev.filter(d => d !== 'medium'));
                        } else {
                          setSelectedDifficulties(prev => [...prev, 'medium']);
                        }
                      }}
                      className="difficulty-button"
                    >
                      Medium
                    </Button>
                    <Button
                      variant={selectedDifficulties.includes('hard') ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => {
                        if (selectedDifficulties.includes('hard')) {
                          setSelectedDifficulties(prev => prev.filter(d => d !== 'hard'));
                        } else {
                          setSelectedDifficulties(prev => [...prev, 'hard']);
                        }
                      }}
                      className="difficulty-button"
                      sx={{
                        '&.MuiButton-contained': {
                          backgroundColor: '#d32f2f !important',
                          color: 'white !important',
                          '&:hover': {
                            backgroundColor: '#b71c1c !important'
                          }
                        },
                        '&.MuiButton-outlined': {
                          borderColor: '#d32f2f !important',
                          color: '#d32f2f !important',
                          '&:hover': {
                            borderColor: '#b71c1c !important',
                            backgroundColor: 'rgba(211, 47, 47, 0.04) !important'
                          }
                        }
                      }}
                    >
                      Hard
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" style={{ marginTop: '8px', textAlign: 'center' }}>
                    Easy: Popular anime (Attack on Titan, Naruto, etc.) • Medium: Well-known but less mainstream • Hard: Obscure/niche anime
                  </Typography>
                </Box>

                {/* Custom Game Section */}
                <Typography variant="h6" gutterBottom className="custom-section-title">
                  Custom Game
                </Typography>
                <Box className="custom-game-controls">
                  <TextField
                    type="number"
                    value={numberOfOpenings}
                    onChange={(e) => setNumberOfOpenings(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1 }}
                    size="small"
                    className="openings-input"
                    label="Openings"
                  />
                  <Button 
                    variant="outlined" 
                    size="large" 
                    onClick={() => handleStartGame()}
                    className="start-game-button custom-start-button"
                    disabled={selectedDifficulties.length === 0}
                  >
                    Start Game ({numberOfOpenings})
                  </Button>
                </Box>
              </Box>
              
              <Button 
                variant="text" 
                onClick={handleBackToMenu}
                className="back-button"
              >
                ← Back to Main Menu
              </Button>
            </Box>
          </div>
        ) : gameFinished ? (
          // Game finished - show final score
          <div className="quiz-paper">
            <Box className="landing-page">
              <Typography variant="h4" gutterBottom color="primary" className="game-complete-title">
                Game Complete!
              </Typography>
              
              <Box className="score-section">
                <Typography variant="h2" color="primary" className="final-score">
                  {gameStats.score} Points
                </Typography>
                
                <Box className="score-chips-container">
                  <Chip 
                    label={`${gameStats.correctGuesses}/${gameStats.totalOpenings} Correct`}
                    color="success"
                    className="score-chip"
                  />
                  <Chip 
                    label={`${Math.round((gameStats.correctGuesses / gameStats.totalOpenings) * 100)}% Accuracy`}
                    color="primary"
                    className="score-chip"
                  />
                </Box>
                
                <Typography variant="body1" color="text.secondary" className="game-complete-message">
                  {gameStats.correctGuesses === gameStats.totalOpenings 
                    ? "Perfect score! You're an anime master!"
                    : gameStats.correctGuesses >= gameStats.totalOpenings * 0.8
                    ? "Excellent work! You know your anime!"
                    : gameStats.correctGuesses >= gameStats.totalOpenings * 0.6
                    ? "Good job! Keep watching more anime!"
                    : gameStats.correctGuesses >= gameStats.totalOpenings * 0.4
                    ? "Not bad! There's room for improvement!"
                    : "Keep practicing! Watch more anime openings!"
                  }
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  Scoring: Easy (10 pts), Medium (15 pts), Hard (20 pts) + up to 5 bonus points for speed
                </Typography>
              </Box>

                              <Box className="final-buttons">
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={handleResetGame}
                    className="play-again-button"
                  >
                    Play Again
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large" 
                    onClick={handleBackToMenu}
                    className="back-to-menu-button"
                  >
                    Main Menu
                  </Button>
                </Box>
            </Box>
          </div>
        ) : loading || (!currentOpening && gameStarted) ? (
          <div className="loading-container">
            <CircularProgress />
          </div>
        ) : currentOpening ? (
          <div className="quiz-paper">
            {/* Game Progress Header */}
            <Box className="game-header">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  Opening {gameStats.currentOpening} of {gameStats.totalOpenings}
                </Typography>
                <Button 
                  variant="text" 
                  size="small"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to quit the game?')) {
                      handleBackToMenu();
                    }
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  Main Menu
                </Button>
              </Box>
              <Box className="game-chips-container">
                <Chip label={`Score: ${gameStats.score}`} color="primary" />
                <Chip label={`Correct: ${gameStats.correctGuesses}`} color="success" />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(gameStats.currentOpening - 1) / gameStats.totalOpenings * 100}
                className="game-progress-bar"
              />
            </Box>

            {/* Status and Timer Container */}
            <Box className="status-timer-container">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" color="text.secondary">
                  {isPlaying ? 'Playing...' : isTimeUp ? (hasGuessed ? (isCorrect ? 'Correct!' : 'Wrong!') : 'Too late!') : 'Loading...'}
                </Typography>
                {currentOpening?.difficulty && isPlaying && (
                  <Chip 
                    label={currentOpening.difficulty.toUpperCase()} 
                    size="small"
                    color={
                      currentOpening.difficulty === 'easy' ? 'success' : 
                      currentOpening.difficulty === 'medium' ? 'primary' : 
                      'error'
                    }
                    sx={{ 
                      fontWeight: 'bold',
                      ...(currentOpening.difficulty === 'hard' && {
                        backgroundColor: '#d32f2f !important',
                        color: 'white !important',
                        '& .MuiChip-label': {
                          color: 'white !important',
                        }
                      })
                    }}
                  />
                )}
              </Box>
              <Box className="timer-progress-container">
                <LinearProgress 
                  variant="determinate" 
                  value={(15 - timeLeft) * (100/15)} 
                  className="timer-progress-bar"
                />
                <Typography variant="body2" color="text.secondary" className="timer-text">
                  {timeLeft}s left
                </Typography>
              </Box>
              {isTimeUp && (
                <Box className="next-button-container">
                  {hasGuessed && (
                    <Typography variant="body2" color="text.secondary" className="points-display">
                      {isCorrect 
                        ? pointBreakdown 
                          ? `${pointBreakdown.base} points for ${pointBreakdown.difficulty} ${pointBreakdown.time > 0 ? `+ ${pointBreakdown.time} for speed` : ''} very nice!`
                          : `+${calculateScore(true, timeLeft, currentOpening?.difficulty || 'hard')} points! ${guessTime && guessTime <= 3 ? 'Lightning fast!' : guessTime && guessTime <= 5 ? 'Very quick!' : 'Good timing!'}`
                        : '+0 points'
                      }
                    </Typography>
                  )}
                  <Button 
                    variant="outlined" 
                    onClick={handleNextOpening}
                    disabled={loading}
                    color="primary"
                    className="next-button"
                  >
                    {loading ? 'Loading...' : gameStats.currentOpening >= gameStats.totalOpenings ? 'Finish Game' : 'Next Opening'}
                  </Button>
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              label="Guess the anime"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isTimeUp || hasGuessed}
              className="search-input"
              ref={searchInputRef}
            />

            {searchResults.length > 0 && !isTimeUp && !hasGuessed && (
              <List className="search-results-list">
                {searchResults.map((result, index) => (
                  <ListItem key={result.id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleGuess(result.title)}
                      selected={index === selectedIndex}
                      className={index === selectedIndex ? 'selected-result' : ''}
                    >
                      <ListItemText primary={result.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {isTimeUp && (
              <Box className="answer-container">
                <Typography 
                  variant="h5" 
                  className={hasGuessed ? (isCorrect ? "success-text" : "error-text") : "warning-text"}
                  gutterBottom
                >
                  {hasGuessed ? (
                                          isCorrect ? "Correct!" : `Wrong! The answer is: ${currentOpening.title}`
                  ) : (
                                          `Too late! The answer is: ${currentOpening.title}`
                  )}
                </Typography>
              </Box>
            )}

            {/* Video Player Container - Hidden until guess is made */}
            <Box className={`video-container ${(hasGuessed || isTimeUp) ? 'visible' : 'hidden'}`}>
              <div id="youtube-player" />
            </Box>
          </div>
        ) : null}
        </Box>
      </Container>
    </>
  );
}

// Add TypeScript declaration for YouTube API
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default App;
